/* eslint-disable lingui/no-unlocalized-strings -- Event names are wire identifiers. */
import { ga4Event, ga4PageView } from "./ga4";
import { markBehaviour, track, trackPageVisit } from "./mixpanel";
import { scrubProperties } from "./pii";

/*
 * The First Round funnel events, sent to Mixpanel and GA4 with the same names and properties.
 * page_viewed is the exception: it goes out as each tool's native page view ($mp_web_page_view
 * in Mixpanel, page_view in GA4) so their built-in page reports keep working.
 *
 * Properties never carry personal data: scrubProperties removes anything that looks like it.
 */
export type FunnelEvents = {
  page_viewed: { page_route: string; page_name: string; page_title?: string };
  cta_clicked: { cta_id: string; cta_location: string; destination?: string };
  trial_started: { trial: "practice_demo"; topic?: string };
  trial_completed: {
    trial: "practice_demo";
    topic?: string;
    reason?: string;
    duration_seconds?: number;
  };
  // entry: where the sign-up came from; has_code / plan_intent: a code or plan carried in.
  signup_completed: { method?: string; entry?: string; has_code?: boolean; plan_intent?: string };
  checkout_started: {
    item_type: "plan" | "pack";
    item_id?: string;
    item_name?: string;
    value?: number;
    currency?: string;
    is_upgrade?: boolean;
    coupon_code?: string;
  };
  // First Round plan dialog (first-round-pricing/PLAN.md v4).
  plan_dialog_opened: { reason: string; mandatory: boolean };
  plan_selected: { plan: string; location: string };
  // location: where the code was entered (the plan dialog leaves it out).
  coupon_applied: { coupon_code: string; location?: string };
  coupon_redeemed: { coupon_code: string; plan: string };
  // A code the server refused when checked (invalid, expired, used up, not for these plans).
  coupon_rejected: { coupon_code: string; reason: string };
  // Marketing films. "inline" is the muted loop in the hero; "sound" is the full film in a dialog.
  video_chapter_viewed: {
    video_id: string;
    chapter: number;
    chapter_name: string;
    mode: "inline" | "sound";
    trigger: "autoplay" | "click";
  };
  video_loop_completed: { video_id: string; loops: number };
  video_sound_opened: { video_id: string; chapter: number };
  // chapter is set by the Loom playlist, where progress is per clip. mode separates the muted
  // autoplay from the sound playback of the same film (the night-before hero film).
  video_progress: {
    video_id: string;
    percent: 25 | 50 | 75 | 100;
    chapter?: number;
    mode?: "muted" | "sound";
  };
  video_closed: { video_id: string; seconds_watched: number };
};

export type FunnelEvent = keyof FunnelEvents;

export const trackEvent = <E extends FunnelEvent>(event: E, properties: FunnelEvents[E]) => {
  const clean = scrubProperties(properties);

  if (event === "page_viewed") {
    trackPageVisit(clean);
    ga4PageView(String(clean.page_route), String(clean.page_name));
    return;
  }

  // Behaviour flags go on before the event, so the event itself already carries them.
  if (event === "video_sound_opened") markBehaviour("watched_film");
  if (event === "trial_started") markBehaviour("tried_demo");

  track(event as Exclude<FunnelEvent, "page_viewed">, clean);
  // GA success events come from the backend, never a button or a return URL.
  if (event !== "signup_completed" && event !== "checkout_started") ga4Event(event, clean);
};

/** Destination for cta_clicked: host + path only. */
export const ctaDestination = (href: string) => {
  try {
    const url = new URL(href, window.location.href);
    return `${url.host}${url.pathname}`;
  } catch {
    return undefined;
  }
};
